/**
 * Changing how a view presents its fields.
 *
 * Generic over the shape, because the endpoint is: a grid stores a width and
 * whether the column is shown, a form stores a label and whether the field is
 * asked for at all, and the write is the same write. Nothing below cares which.
 *
 * Order, width and whether a column is shown at all. None of the three changes
 * which rows come back or in what order, so none of them is in the row query's
 * signature and none of them costs a refetch: the grid draws different columns
 * over exactly the rows it was already holding.
 *
 * **Applied before the request and rolled back if it is refused.** A column that
 * stays visible for a round trip after being hidden, or a divider that snaps
 * back to where it was and then jumps to where it was dragged, is a control that
 * reads as broken. The acting tab is excluded from its own
 * `view_field_options_updated` broadcast, so nothing is coming over the socket
 * to do this instead.
 *
 * **The answer is not written back.** These options have no server-side
 * derivation -- the endpoint stores what it is given -- so the response is the
 * value already on screen, arriving by a longer road. Writing it back would also
 * make two changes in flight at once a race, where the earlier answer describes
 * the view before the later change and puts it back.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { ViewFieldOptions } from '@/api/types'
import { withFieldOptions } from './field-options'

export function useUpdateFieldOptions<T>(viewId: number) {
  const queryClient = useQueryClient()
  const key = qk.fieldOptions(viewId)

  return useMutation({
    mutationFn: (changes: Record<number, Partial<T>>): Promise<unknown> =>
      unwrap(
        getApi().PATCH('/api/database/views/{view_id}/field-options/', {
          params: { path: { view_id: viewId } },
          // Cast at the boundary, like every field payload: the body is a union
          // over the seven view types and the compiler cannot know which one a
          // view id names.
          body: { field_options: changes } as never,
        }),
      ),

    onMutate: (changes) => {
      // No `cancelQueries` before the write, which is what an optimistic update
      // usually opens with. There is nothing to cancel: these options are read
      // once with `staleTime: Infinity`, refetching on focus is off, and
      // nothing in the application invalidates them -- so the only fetch there
      // has ever been is the first one, and the controls that write them do not
      // render until it has landed.
      const previous = queryClient.getQueryData<ViewFieldOptions>(key)
      queryClient.setQueryData<ViewFieldOptions>(
        key,
        withFieldOptions<T>(previous, changes),
      )
      return { previous }
    },

    onError: (_error, _changes, context) => {
      // Back to exactly what was there, which may be nothing at all: a view
      // whose options had not arrived yet has no entry, and writing an empty
      // one would say the options are known to be empty.
      queryClient.setQueryData<ViewFieldOptions | undefined>(key, context?.previous)
    },
  })
}
